import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, catalogContextUserStorageKey } from "../Config/firebase";
import axios from "axios";
import logo from "../../assets/logo.png";

import SaveAsIcon from "@mui/icons-material/SaveAs";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    IconButton,
    Box,
    useMediaQuery,
    Menu,
    MenuItem,
} from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Menu as MenuIcon,
    Discount as DiscountIcon,
    ChecklistOutlined,
} from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import { styled } from "@mui/material/styles";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CampaignIcon from "@mui/icons-material/Campaign";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import MessageIcon from "@mui/icons-material/Message";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import config from "../Config/config";

const drawerWidth = 260;
const APP_VERSION = "v4.1.1";

const SidebarContainer = styled("div")(() => ({
    display: "flex",
}));

const SidebarHeader = styled("div")(({ sidebarOpen }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: sidebarOpen ? 16 : 10,
    background: "linear-gradient(to bottom right, #333999, #5555bb)",
    color: "#fff",
    textAlign: "center",
    minHeight: sidebarOpen ? 190 : 80,
    position: "relative",
    overflow: "hidden",
}));

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = localStorage.getItem("userId");

    const [sidebarOpen, setSidebarOpen] = useState(
        localStorage.getItem("sidebarOpen") === "true"
    );
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [permissions, setPermissions] = useState({});
    const [tokens, setTokens] = useState(localStorage.getItem("tokens") || "0");
    const [hibidEnabled, setHibidEnabled] = useState(
        localStorage.getItem("hibid") === "true"
    );

    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const settingsMenuOpen = Boolean(settingsAnchorEl);

    const isMobile = useMediaQuery("(max-width:600px)");

    const drawerPaperRef = useRef(null);
    const settingsMenuPaperRef = useRef(null);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const target = event.target;
            const drawerEl = drawerPaperRef.current;
            const menuEl = settingsMenuPaperRef.current;

            if (drawerEl && drawerEl.contains(target)) return;
            if (menuEl && menuEl.contains(target)) return;

            setSettingsAnchorEl(null);

            if (isMobile) {
                if (mobileDrawerOpen) setMobileDrawerOpen(false);
            } else if (sidebarOpen) {
                setSidebarOpen(false);
                localStorage.setItem("sidebarOpen", "false");
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [isMobile, mobileDrawerOpen, sidebarOpen]);

    useEffect(() => {
        const handleStorageChange = () => {
            const open = localStorage.getItem("sidebarOpen") === "true";
            setSidebarOpen(open);

            const storedTokens = localStorage.getItem("tokens");
            if (storedTokens != null) {
                setTokens(storedTokens);
            }

            setHibidEnabled(localStorage.getItem("hibid") === "true");
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await axios.get(
                    `${config.API_BASE_URL}/get-user-details?userId=${userId}`
                );
                const { tokens, permissions } = response.data;
                const hibid = response?.data?.hibid === true;

                setPermissions(permissions || {});
                setHibidEnabled(hibid);
                localStorage.setItem("tokens", tokens);
                localStorage.setItem("hibid", String(hibid));
                setTokens(tokens ?? "0");
            } catch (error) {
                console.error("Error fetching user details:", error.message);
            }
        };

        if (userId) {
            fetchUserDetails();
        }
    }, [userId]);

    const handleLogout = async () => {
        try {
            localStorage.removeItem(catalogContextUserStorageKey);
            localStorage.removeItem("admin");
            localStorage.removeItem("originalUserId");
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    const handleSidebarChange = () => {
        const next = !sidebarOpen;
        setSidebarOpen(next);
        localStorage.setItem("sidebarOpen", String(next));
    };

    const handleOpenSettingsMenu = (event) => {
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleCloseSettingsMenu = () => {
        setSettingsAnchorEl(null);
    };

    const goSettingsSection = (section) => {
        navigate("/usersettings", { state: { activeSection: section } });
        if (isMobile) setMobileDrawerOpen(false);
        handleCloseSettingsMenu();
    };

    const goExternal = (url) => {
        window.open(url, "_blank");
        handleCloseSettingsMenu();
    };

    const navItemSx = {
        borderRadius: 2,
        mx: 1,
        my: 0.25,
        px: 1.5,
        "& .MuiListItemText-primary": {
            fontSize: "0.88rem",
            letterSpacing: "0.01em",
            color: "#f9fafb",
        },
        "& svg": {
            fontSize: 22,
            color: "#e5e7ff",
        },
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.12)",
        },
        "&.Mui-selected": {
            backgroundColor: "rgba(255,255,255,0.2)",
            "& .MuiListItemText-primary": {
                color: "#ffffff",
                fontWeight: 600,
            },
            "& svg": {
                color: "#ffffff",
            },
        },
    };

    const closeSidebarAfterNavigate = () => {
        if (isMobile) setMobileDrawerOpen(false);
    };

    const isHibidRoute = location.pathname === "/hibid" || location.pathname.startsWith("/hibid/");
    const isAuctionFuelRoute =
        location.pathname === "/auctionfuel" || location.pathname.startsWith("/auctionfuel/");

    const hibidNavItems = [
        {
            key: "hibid-users",
            text: "Users",
            icon: <PeopleAltOutlinedIcon />,
            path: "/hibid/users",
            selected: location.pathname === "/hibid/users",
        },
        {
            key: "hibid-auctions",
            text: "Auctions",
            icon: <GavelIcon />,
            path: "/hibid/auctions",
            selected: location.pathname === "/hibid/auctions" || location.pathname.startsWith("/hibid/auctions/"),
        },
        {
            key: "hibid-daily-registration",
            text: "Daily Registration",
            icon: <CalendarMonthIcon />,
            path: "/hibid/daily-registration",
            selected: location.pathname === "/hibid/daily-registration",
        },
        {
            key: "hibid-product-search",
            text: "Product Search",
            icon: <SearchIcon />,
            path: "/hibid/product-search",
            selected: location.pathname === "/hibid/product-search",
        },
        {
            key: "hibid-definitions",
            text: "Tier & LCV Definitions",
            icon: <MenuBookIcon />,
            path: "/hibid/definitions",
            selected: location.pathname === "/hibid/definitions",
        },
        {
            key: "hibid-back-to-explorer",
            text: "Back to Explorer",
            icon: <ArrowBackIcon />,
            path: "/",
            selected: false,
        },
    ];

    const auctionFuelNavItems = [
        {
            key: "auctionfuel-overview",
            text: "Overview",
            icon: <Inventory2OutlinedIcon />,
            path: "/auctionfuel",
            selected: location.pathname === "/auctionfuel",
        },
        {
            key: "auctionfuel-auctions",
            text: "My Auctions",
            icon: <GavelIcon />,
            path: "/auctionfuel/auctions",
            selected:
                location.pathname === "/auctionfuel/auctions" ||
                location.pathname.startsWith("/auctionfuel/auctions/"),
        },
        {
            key: "auctionfuel-help",
            text: "Help",
            icon: <SupportAgentIcon />,
            path: "/auctionfuel/help",
            selected:
                location.pathname === "/auctionfuel/help" ||
                location.pathname.startsWith("/auctionfuel/help/"),
        },
        {
            key: "auctionfuel-user-data",
            text: "User Data",
            icon: <ManageAccountsOutlinedIcon />,
            path: "/auctionfuel/user-data",
            selected:
                location.pathname === "/auctionfuel/user-data" ||
                location.pathname.startsWith("/auctionfuel/user-data/"),
        },
        {
            key: "auctionfuel-back-to-explorer",
            text: "Back to Explorer",
            icon: <ArrowBackIcon />,
            path: "/",
            selected: false,
        },
    ];

    const isSettingsSectionActive = (sectionKey) => {
        if (location.pathname !== "/usersettings") return false;
        return location.state?.activeSection === sectionKey;
    };

    const renderNavItem = ({ key, text, icon, path, external, state, onClick, selected }) => (
        <ListItem disablePadding key={key}>
            <ListItemButton
                sx={navItemSx}
                selected={
                    typeof selected === "boolean"
                        ? selected
                        : path === "/usersettings"
                            ? isSettingsSectionActive(state)
                            : location.pathname === path
                }
                onClick={() => {
                    if (onClick) {
                        onClick();
                        closeSidebarAfterNavigate();
                        return;
                    }

                    if (external) {
                        window.open(path, "_blank");
                        closeSidebarAfterNavigate();
                        return;
                    }

                    navigate(path, {
                        state: state ? { activeSection: state } : undefined,
                    });
                    closeSidebarAfterNavigate();
                }}
            >
                {React.cloneElement(icon, {
                    sx: {
                        marginRight: sidebarOpen || isMobile ? 1 : 0,
                        fontSize: 22,
                    },
                })}
                {(sidebarOpen || isMobile) && <ListItemText primary={text} />}
            </ListItemButton>
        </ListItem>
    );

    const sidebarContent = (
        <Box
            className="flex flex-col"
            sx={{ pt: 1, pb: 0, height: "100%" }}
        >
            <Box sx={{ flex: 1, overflowY: "auto", pb: 1.5 }}>
                <List sx={{ pt: 0.5, pb: 0 }}>
                    {isHibidRoute
                        ? hibidNavItems.map(renderNavItem)
                        : isAuctionFuelRoute
                            ? auctionFuelNavItems.map(renderNavItem)
                            : (
                                <>
                                    {(permissions.superAdmin ||
                                        localStorage.getItem("admin") === "true") && (
                                            <ListItem disablePadding key="admin">
                                                <ListItemButton
                                                    sx={navItemSx}
                                                    selected={location.pathname === "/admin"}
                                                    onClick={() => {
                                                        navigate("/admin");
                                                        closeSidebarAfterNavigate();
                                                    }}
                                                >
                                                    <AdminPanelSettingsIcon
                                                        sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                                                    />
                                                    {(sidebarOpen || isMobile) && <ListItemText primary="Admin" />}
                                                </ListItemButton>
                                            </ListItem>
                                        )}

                                    {(hibidEnabled ||
                                        permissions.superAdmin ||
                                        localStorage.getItem("admin") === "true") && (
                                            <ListItem disablePadding key="auctionfuel-admin">
                                                <ListItemButton
                                                    sx={navItemSx}
                                                    selected={location.pathname === "/auctionfuel" || location.pathname.startsWith("/auctionfuel/")}
                                                    onClick={() => {
                                                        navigate("/auctionfuel/auctions");
                                                        closeSidebarAfterNavigate();
                                                    }}
                                                >
                                                    <GavelIcon
                                                        sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                                                    />
                                                    {(sidebarOpen || isMobile) && <ListItemText primary="AuctionFuel" />}
                                                </ListItemButton>
                                            </ListItem>
                                        )}

                                    <ListItem disablePadding key="hibid">
                                        <ListItemButton
                                            sx={navItemSx}
                                            selected={location.pathname === "/hibid" || location.pathname.startsWith("/hibid/")}
                                            onClick={() => {
                                                navigate("/hibid");
                                                closeSidebarAfterNavigate();
                                            }}
                                        >
                                            <ChecklistOutlined
                                                sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                                            />
                                            {(sidebarOpen || isMobile) && <ListItemText primary="hibid" />}
                                        </ListItemButton>
                                    </ListItem>

                                    {permissions.superAdmin && (
                                        <ListItem disablePadding key="Discount">
                                            <ListItemButton
                                                sx={navItemSx}
                                                selected={location.pathname === "/discount"}
                                                onClick={() => {
                                                    navigate("/discount");
                                                    closeSidebarAfterNavigate();
                                                }}
                                            >
                                                <DiscountIcon
                                                    sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                                                />
                                                {(sidebarOpen || isMobile) && (
                                                    <ListItemText primary="Discount" />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    )}

                                    {(permissions.superAdmin ||
                                        localStorage.getItem("admin") === "true") && (
                                            <ListItem disablePadding key="email">
                                                <ListItemButton
                                                    sx={navItemSx}
                                                    selected={location.pathname === "/email"}
                                                    onClick={() => {
                                                        navigate("/email");
                                                        closeSidebarAfterNavigate();
                                                    }}
                                                >
                                                    <EmailIcon
                                                        sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                                                    />
                                                    {(sidebarOpen || isMobile) && <ListItemText primary="Email" />}
                                                </ListItemButton>
                                            </ListItem>
                                        )}

                                    {[
                                        {
                                            key: "catalogPhotos",
                                            text: "Catalog with AI/UPC",
                                            icon: <CameraAltIcon />,
                                            path: "/createpods",
                                        },
                                        {
                                            key: "explorerAiChat",
                                            text: "Explorer AI Chat",
                                            icon: <MessageIcon />,
                                            onClick: () => window.dispatchEvent(new Event("open-explorer-ai-chat")),
                                            selected: false,
                                        },
                                        {
                                            key: "buymoretokens",
                                            text: "Buy More Tokens",
                                            icon: <ShoppingCartIcon />,
                                            path: "/usersettings",
                                            state: "tokens",
                                        },
                                        {
                                            key: "createAiTone",
                                            text: "Create/Edit AI Prompt",
                                            icon: <CampaignIcon />,
                                            path: "/usersettings",
                                            state: "settings",
                                        },
                                        {
                                            key: "supportTickets",
                                            text: "Create Support Tickets",
                                            icon: <SupportAgentIcon />,
                                            path: "/support-tickets",
                                        },
                                    ].filter((item) => item.key !== "auctionfuel" || hibidEnabled).map(renderNavItem)}
                                </>
                            )}
                </List>
            </Box>

            <Box
                sx={{
                    borderTop: "1px solid rgba(255,255,255,0.25)",
                    pt: 0.5,
                    pb: 0.5,
                    mt: "auto",
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                    background: "linear-gradient(to bottom right, #333999, #5555bb)",
                }}
            >
                <ListItem disablePadding key="settings-menu">
                    <ListItemButton sx={navItemSx} onClick={handleOpenSettingsMenu}>
                        <SettingsIcon
                            sx={{ marginRight: sidebarOpen || isMobile ? 1 : 0 }}
                        />
                        {(sidebarOpen || isMobile) && <ListItemText primary="Settings" />}
                    </ListItemButton>
                </ListItem>
            </Box>

            <Menu
                anchorEl={settingsAnchorEl}
                open={settingsMenuOpen}
                onClose={handleCloseSettingsMenu}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                PaperProps={{
                    ref: settingsMenuPaperRef,
                    sx: {
                        mt: 1,
                        borderRadius: "0.9rem",
                        border: "1px solid rgba(148,163,184,0.45)",
                        boxShadow: "0 12px 30px rgba(15,23,42,0.25)",
                        background:
                            "linear-gradient(to bottom right, #f9fafb, #eff6ff, #eef2ff)",
                        minWidth: 220,
                    },
                }}
            >
                <MenuItem onClick={() => goSettingsSection("profile")}>
                    <SettingsIcon sx={{ fontSize: 20, mr: 1, color: "#333999" }} />
                    Account Settings
                </MenuItem>

                <MenuItem
                    onClick={() =>
                        goExternal("https://listernaut.com/pages/e-commerce-tutorials")
                    }
                >
                    <WarningAmberIcon sx={{ fontSize: 20, mr: 1, color: "#f97316" }} />
                    Help
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate("/clear-cache-guide");
                        if (isMobile) setMobileDrawerOpen(false);
                        handleCloseSettingsMenu();
                    }}
                >
                    <WarningAmberIcon sx={{ fontSize: 20, mr: 1, color: "#eab308" }} />
                    Clear Cache Guide
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        window.open("/documentation", "_blank");
                        handleCloseSettingsMenu();
                    }}
                >
                    <DescriptionIcon sx={{ fontSize: 20, mr: 1, color: "#1d4ed8" }} />
                    Documentation
                </MenuItem>

                {(permissions.documents ||
                    localStorage.getItem("admin") === "true") && (
                        <MenuItem
                            onClick={() => {
                                window.open("/docs", "_blank");
                                handleCloseSettingsMenu();
                            }}
                        >
                            <SaveAsIcon sx={{ fontSize: 20, mr: 1, color: "#4f46e5" }} />
                            Create Documentation
                        </MenuItem>
                    )}

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    onClick={() => {
                        handleCloseSettingsMenu();
                        handleLogout();
                    }}
                >
                    <HighlightOffIcon sx={{ fontSize: 20, mr: 1, color: "#b91c1c" }} />
                    Logout
                </MenuItem>
            </Menu>
        </Box>
    );

    const desktopSidebar = (
        <Drawer
            variant="permanent"
            PaperProps={{ ref: drawerPaperRef }}
            sx={{
                width: sidebarOpen ? drawerWidth : 84,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: sidebarOpen ? drawerWidth : 84,
                    transition: "width 0.3s ease-in-out",
                    boxSizing: "border-box",
                    background: "linear-gradient(to bottom right, #333999, #5555bb)",
                    color: "#eef2ff",
                    overflowX: "hidden",
                    borderRight: "none",
                },
            }}
        >
            <SidebarHeader sidebarOpen={sidebarOpen}>
                <IconButton
                    onClick={handleSidebarChange}
                    sx={{
                        position: "absolute",
                        top: 30,
                        right: 12,
                        color: "#eef2ff",
                        backgroundColor: "rgba(15,23,42,0.2)",
                        "&:hover": { backgroundColor: "rgba(15,23,42,0.3)" },
                    }}
                >
                    {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>

                {sidebarOpen && (
                    <img
                        onClick={() => navigate("/")}
                        src={logo}
                        alt="Logo"
                        className="w-[100px] h-[100px] rounded-full mb-2 cursor-pointer bg-white mt-4"
                    />
                )}

                {sidebarOpen && (
                    <span
                        className="text-[0.75rem] text-slate-100/90 -mt-1 mb-1"
                        style={{ letterSpacing: "0.08em" }}
                    >
                        {APP_VERSION}
                    </span>
                )}

                {sidebarOpen && (
                    <Box
                        sx={{
                            mt: 0.5,
                            mb: 2,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                px: 1.75,
                                py: 0.75,
                                borderRadius: "999px",
                                backgroundColor: "rgba(255,255,255,0.18)",
                                border: "1px solid rgba(209,213,219,0.6)",
                            }}
                        >
                            <span className="text-[0.65rem] uppercase tracking-[0.16em] text-slate-100">
                                Available Tokens
                            </span>
                            <span className="text-sm  text-white mt-0.5">
                                {tokens}
                            </span>
                        </Box>
                    </Box>
                )}
            </SidebarHeader>

            <Divider sx={{ backgroundColor: "rgba(226,232,240,0.6)" }} />
            {sidebarContent}
        </Drawer>
    );

    const mobileSidebar = (
        <>
            {!mobileDrawerOpen && (
                <IconButton
                    onClick={() => setMobileDrawerOpen(true)}
                    sx={{
                        position: "fixed",
                        top: 16,
                        left: 16,
                        zIndex: 1300,
                        background:
                            "linear-gradient(to bottom right, #333999, #5555bb)",
                        color: "#fff",
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            <Drawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                PaperProps={{ ref: drawerPaperRef }}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        background:
                            "linear-gradient(to bottom right, #333999, #5555bb)",
                        color: "#eef2ff",
                    },
                }}
            >
                <SidebarHeader sidebarOpen={true}>
                    <IconButton
                        onClick={() => setMobileDrawerOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            color: "#eef2ff",
                            backgroundColor: "rgba(15,23,42,0.2)",
                            "&:hover": { backgroundColor: "rgba(15,23,42,0.3)" },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <img
                        onClick={() => {
                            navigate("/");
                            setMobileDrawerOpen(false);
                        }}
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 96,
                            height: 96,
                            borderRadius: "100%",
                            marginBottom: 8,
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                        }}
                    />

                    <span
                        className="text-[0.75rem] text-slate-100/90 -mt-1 mb-1"
                        style={{ letterSpacing: "0.08em" }}
                    >
                        {APP_VERSION}
                    </span>

                    <Box
                        sx={{
                            mt: 0.5,
                            mb: 2,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                px: 1.75,
                                py: 0.75,
                                borderRadius: "999px",
                                backgroundColor: "rgba(255,255,255,0.18)",
                                border: "1px solid rgba(209,213,219,0.6)",
                            }}
                        >
                            <span className="text-[0.65rem] uppercase tracking-[0.16em] text-slate-100">
                                Available Tokens
                            </span>
                            <span className="text-sm  text-white mt-0.5">
                                {tokens}
                            </span>
                        </Box>
                    </Box>
                </SidebarHeader>

                <Divider sx={{ backgroundColor: "rgba(226,232,240,0.6)" }} />
                {sidebarContent}
            </Drawer>
        </>
    );

    return (
        <SidebarContainer>
            {isMobile ? mobileSidebar : desktopSidebar}
        </SidebarContainer>
    );
};

export default Sidebar;
